import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import { getAdminDatasourceStats } from "../../services/apiService";
import { POPPINS, HEADER_COLOR } from "./adminConstants";

// One colour per datasource (cycles if > palette length)
const PALETTE = [
  { bg: "#e3f2fd", border: "#90caf9", text: "#1565c0" },
  { bg: "#e8f5e9", border: "#a5d6a7", text: "#2e7d32" },
  { bg: "#fff3e0", border: "#ffcc80", text: "#e65100" },
  { bg: "#fce4ec", border: "#f48fb1", text: "#880e4f" },
  { bg: "#ede7f6", border: "#ce93d8", text: "#4a148c" },
  { bg: "#e0f7fa", border: "#80deea", text: "#006064" },
  { bg: "#f3e5f5", border: "#ce93d8", text: "#6a1b9a" },
  { bg: "#fff8e1", border: "#ffe082", text: "#f57f17" },
];

const DatasourceBreakdown = ({ selectedDatasource, onSelect, userId }) => {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getAdminDatasourceStats(userId ? { user_id: userId } : undefined);
      if (res?.error) { setError(res.error); return; }
      setData(res.data ?? []);
    } catch { setError("Failed to load datasource stats."); }
    finally   { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 3, pb: 1.5 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" sx={{ ...POPPINS, color: "#999" }}>Loading datasource stats…</Typography>
      </Box>
    );
  }

  if (error || data.length === 0) return null;

  const total = data.reduce((s, d) => s + d.total, 0);

  return (
    <Box sx={{ px: 3, pb: 2 }}>
      {/* Section label */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, mt: 1 }}>
        <StorageIcon sx={{ fontSize: 14, color: "#888" }} />
        <Typography variant="caption" sx={{ ...POPPINS, color: "#888", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Questions by Datasource
        </Typography>
        <Typography variant="caption" sx={{ ...POPPINS, color: "#bbb", fontSize: "0.68rem" }}>
          ({total.toLocaleString()} total)
        </Typography>
      </Box>

      {/* Chip row */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {/* "All" chip */}
        <Chip
          label="All"
          size="small"
          onClick={() => onSelect && onSelect(null)}
          variant={!selectedDatasource ? "filled" : "outlined"}
          sx={{
            ...POPPINS,
            fontSize: "0.72rem",
            height: 26,
            fontWeight: !selectedDatasource ? 700 : 500,
            backgroundColor: !selectedDatasource ? HEADER_COLOR : "transparent",
            color: !selectedDatasource ? "#fff" : HEADER_COLOR,
            borderColor: HEADER_COLOR,
            cursor: "pointer",
          }}
        />

        {data.map((item, i) => {
          const { bg, border, text } = PALETTE[i % PALETTE.length];
          const isActive = selectedDatasource === item.datasource;
          const pct = total > 0 ? ((item.total / total) * 100).toFixed(1) : 0;
          return (
            <Tooltip key={item.datasource} title={`${item.total.toLocaleString()} questions (${pct}% of total)`}>
              <Chip
                label={`${item.datasource} · ${item.total.toLocaleString()}`}
                size="small"
                onClick={() => onSelect && onSelect(isActive ? null : item.datasource)}
                variant={isActive ? "filled" : "outlined"}
                sx={{
                  ...POPPINS,
                  fontSize: "0.72rem",
                  height: 26,
                  fontWeight: isActive ? 700 : 500,
                  backgroundColor: isActive ? text : bg,
                  color: isActive ? "#fff" : text,
                  borderColor: border,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: isActive ? text : border },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default DatasourceBreakdown;
